import { DefaultFlightSeatFragment } from '../../../../graphql/generated/graphql';

/**
 * Get the seat row name, or an empty string when we were never told one. Seats read off a booked PNR only carry
 * a row position when the airline sent an aisle/window characteristic, so falling back to "middle" would tell the
 * traveler where they are sitting on no evidence at all.
 */
export function getSeatRowName(seatLocation: DefaultFlightSeatFragment | undefined): string {
  if (!seatLocation) {
    return '';
  }

  if (seatLocation.rowLocation.isWindow) {
    return 'WINDOW';
  }

  if (seatLocation.rowLocation.isAisle) {
    return 'AISLE';
  }

  if (seatLocation.rowLocation.isMiddle) {
    return 'MIDDLE';
  }

  return '';
}

/**
 * Get the seat deck location, or an empty string when we were never told one. Only a seat map places a seat on the
 * deck; a booked PNR never does, so "rear" as a fallback was a guess presented to the traveler as fact.
 */
export function getLocationName(seatLocation: DefaultFlightSeatFragment | undefined): string {
  if (!seatLocation) {
    return '';
  }

  if (seatLocation.deckLocation.isExitRow) {
    return 'EXIT ROW';
  }

  if (seatLocation.deckLocation.isOverWing) {
    return 'OVER WING';
  }

  if (seatLocation.deckLocation.isFront) {
    return 'FRONT';
  }

  if (seatLocation.deckLocation.isRear) {
    return 'REAR';
  }

  return '';
}

/**
 * The positions we actually know for a seat, in display order. Callers join these into the parenthetical after the
 * designator and drop it entirely when the list is empty, so a seat we know little about reads as plain "23D".
 */
export function getSeatPositions(seatLocation: DefaultFlightSeatFragment | undefined): string[] {
  return [getSeatRowName(seatLocation), getLocationName(seatLocation)].filter((position) => position !== '');
}
